import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from './api';
import { usePaneQuery } from './filter-context';
import type { PaneQuery } from '../../shared/contracts';

export const useAuth = () => useQuery({ queryKey: ['auth'], queryFn: api.authState });
export const useHealth = () => useQuery({ queryKey: ['health'], queryFn: api.health });

/**
 * Pane query options: keyed by id + active query, a silent 30s background refetch, and
 * `keepPreviousData` so a period change shows the prior pane while the new one loads.
 * (PaneLoader additionally retains a last-good value to survive a failed refresh OR a
 * period change whose fetch then errors — neither blanks a good pane.)
 */
export function paneQueryOptions(id: string, query: PaneQuery) {
  return {
    queryKey: ['pane', id, query] as const,
    queryFn: () => api.pane(id, query),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  };
}

/** Loads a pane under the active period/filter query; refetches when the query changes. */
export const usePane = (id: string) => {
  const { query } = usePaneQuery();
  return useQuery(paneQueryOptions(id, query));
};
